import { GridRowModes, GridToolbarContainer, GridActionsCellItem, useGridApiRef } from '@mui/x-data-grid-premium';
import { Field, FieldArray, useFormikContext } from 'formik';
import { v4 as uuidv4 } from 'uuid';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';

import { dataGridPremiumColumns } from '../../../utils/helper';

import {
  DataGridIdentifiers,
  FormErrorUI,
  AddButtonUi,
  DatagridPremiumUi,
  InputUI,
  DatePickerUI,
} from '../../Interface';
import dayjs from 'dayjs';
import { Box, Tooltip } from '@mui/material';

function EditToolbar(props) {
  const { disabled, push, defPermanentAction } = props;
  const { values, validateField } = useFormikContext();

  const handleClick = async () => {
    const newData = {
      ...defPermanentAction,
      id: uuidv4(),
    };

    await push(newData);

    const fieldsToValidate = [
      `permanentAction.${values.permanentAction.length ?? 0}.name`,
      `permanentAction.${values.permanentAction.length ?? 0}.responsiblePerson`,
      `permanentAction.${values.permanentAction.length ?? 0}.date`,
      `permanentAction.${values.permanentAction.length ?? 0}.status`,
    ];

    for (const field of fieldsToValidate) {
      await validateField(field);
    }
  };

  return (
    <GridToolbarContainer>
      <AddButtonUi disabled={disabled} onClick={handleClick} />
    </GridToolbarContainer>
  );
}

const ComplaintReportD5 = ({ disabled, defPermanentAction, setNoteDialogState }) => {
  const apiRef = useGridApiRef();
  const { values, validateField, setFieldValue, errors } = useFormikContext();

  const getValueIndex = (values, id) => {
    return values.permanentAction.findIndex((rc) => rc.id === id);
  };

  const columns = (setFieldValue, validateField, errors, remove) => {
    return dataGridPremiumColumns([
      {
        field: 'name',
        headerName: 'Intézkedés',
        type: 'string',
        editable: true,
        renderCell: (params) => {
          const value = params?.row?.name ?? '';
          return (
            <Tooltip title={value}>
              <Box
                sx={{
                  display: '-webkit-box',
                  WebkitLineClamp: 1,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                }}
              >
                {value}
              </Box>
            </Tooltip>
          );
        },
        renderEditCell: (data) => {
          const valueIndex = getValueIndex(values, data.id);

          return (
            <Field
              name={`permanentAction.${valueIndex}.name`}
              type="text"
              variant="standard"
              as={InputUI}
              fullWidth
              helperText={<FormErrorUI message={errors?.permanentAction?.[valueIndex]?.name} />}
              onChange={(e) => {
                setFieldValue(`permanentAction.${valueIndex}.name`, e.target.value ?? '');
                data.api.setEditCellValue({
                  id: data.id,
                  field: data.field,
                  value: e.target.value,
                });
              }}
              onBlur={async () => await validateField(`permanentAction.${valueIndex}.name`)}
            />
          );
        },
      },
      {
        field: 'date',
        headerName: 'Dátum',
        type: 'date',
        editable: true,
        valueGetter: (_data, row) => {
          return row.date ? new Date(row.date) : '';
        },
        renderEditCell: (data) => {
          const valueIndex = getValueIndex(values, data.id);
          return (
            <Field
              name={`permanentAction.${valueIndex}.date`}
              component={DatePickerUI}
              helperText={<FormErrorUI message={errors?.permanentAction?.[valueIndex]?.date} />}
              variant="standard"
              value={dayjs(data.row.date)}
              onChange={(newDate) => {
                const finalDate = newDate ? newDate.startOf('day').add(12, 'hour').toDate() : null;

                setFieldValue(`permanentAction.${valueIndex}.date`, finalDate);

                data.api.setEditCellValue({
                  id: data.id,
                  field: data.field,
                  value: finalDate,
                });
              }}
              onBlur={async () => await validateField(`permanentAction.${valueIndex}.date`)}
            />
          );
        },
      },
      {
        field: 'responsiblePerson',
        headerName: 'Felelős',
        type: 'string',
        editable: true,
        renderCell: (data) => {
          const valueIndex = getValueIndex(values, data.id);
          return values.permanentAction?.[valueIndex]?.responsiblePerson;
        },
        renderEditCell: (data) => {
          const valueIndex = getValueIndex(values, data.id);

          return (
            <Field
              name={`permanentAction.${valueIndex}.responsiblePerson`}
              type="text"
              variant="standard"
              as={InputUI}
              fullWidth
              helperText={<FormErrorUI message={errors?.permanentAction?.[valueIndex]?.responsiblePerson} />}
              onChange={(e) => {
                setFieldValue(`permanentAction.${valueIndex}.responsiblePerson`, e.target.value ?? '');
                data.api.setEditCellValue({
                  id: data.id,
                  field: data.field,
                  value: e.target.value,
                });
              }}
              onBlur={async () => await validateField(`permanentAction.${valueIndex}.responsiblePerson`)}
            />
          );
        },
      },
      {
        field: 'status',
        headerName: 'Állapot',
        type: 'string',
        editable: true,
        renderCell: (data) => {
          const valueIndex = getValueIndex(values, data.id);
          return values.permanentAction?.[valueIndex]?.status;
        },
        renderEditCell: (data) => {
          const valueIndex = getValueIndex(values, data.id);

          return (
            <Field
              name={`permanentAction.${valueIndex}.status`}
              type="text"
              variant="standard"
              as={InputUI}
              fullWidth
              helperText={<FormErrorUI message={errors?.permanentAction?.[valueIndex]?.status} />}
              onChange={(e) => {
                setFieldValue(`permanentAction.${valueIndex}.status`, e.target.value ?? '');
                data.api.setEditCellValue({
                  id: data.id,
                  field: data.field,
                  value: e.target.value,
                });
              }}
              onBlur={async () => await validateField(`permanentAction.${valueIndex}.status`)}
            />
          );
        },
      },
      {
        field: 'actions',
        type: 'actions',
        headerName: 'Szerk.',
        cellClassName: 'actions',
        sortable: false,
        getActions: ({ id }) => {
          return [
            <GridActionsCellItem
              icon={<DeleteIcon />}
              label="Delete"
              onClick={() => {
                const valueIndex = getValueIndex(values, id);
                return remove(valueIndex);
              }}
              color="inherit"
              disabled={disabled}
            />,
          ];
        },
      },
    ]);
  };

  return (
    <div className="overflow-x-auto">
      <div className="align-middle">
        <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 sm:rounded-lg">
          <FieldArray name="permanentAction">
            {({ remove, push }) => (
              <DatagridPremiumUi
                apiRef={apiRef}
                datagridIdentifier={DataGridIdentifiers.ComplaintReportPermanentAction}
                height={400}
                columns={columns(setFieldValue, validateField, errors, remove)}
                rows={values?.permanentAction ?? []}
                slots={{
                  toolbar: EditToolbar,
                }}
                headerFilters={false}
                slotProps={{
                  toolbar: {
                    disabled,
                    push,
                    defPermanentAction,
                  },
                }}
                onCellDoubleClick={(params, event) => {
                  event.defaultMuiPrevented = true;
                  if (disabled) return;
                  if (params.field === 'name') {
                    event.defaultMuiPrevented = true;
                    const valueIndex = getValueIndex(values, params.row.id);
                    setNoteDialogState({ open: true, fieldName: `permanentAction.${valueIndex}.name` });
                    return;
                  }
                }}
                editMode="row"
                getRowHeight={() => 'auto'}
                onCellClick={(params, event) => {
                  if (disabled) return;
                  const rowMode = apiRef.current.getRowMode(params.row.id);
                  if (rowMode === GridRowModes.View) apiRef.current.startRowEditMode({ id: params.row.id });
                }}
              />
            )}
          </FieldArray>
        </div>
      </div>
    </div>
  );
};

export default ComplaintReportD5;
